/**
 * SuperAdmin Platform page — tabs host.
 *
 *   /admin/super/platform-pnl              → Dashboard tab (default)
 *   /admin/super/platform-pnl?tab=daily    → Daily PnL tab   (TEMP disabled — see DISABLED_TABS)
 *   /admin/super/platform-pnl?tab=roster   → User Roster tab (TEMP disabled — see DISABLED_TABS)
 *
 * The route filename keeps the historical name to avoid breaking deep
 * links; the host mounts one of three tab components based on the `tab`
 * query param (Dashboard when absent). A disabled tab (see DISABLED_TABS)
 * is hidden from the bar and its deep link falls back to Dashboard. Each
 * tab owns its own data fetching, filter state, and registers Refresh /
 * Export-CSV actions back into this page's PageHeader via the
 * `registerActions` callback.
 *
 * Auth gating happens once at the host (server + client), so the tab
 * components don't need to repeat it.
 */
import React, { useState, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Home } from 'lucide-react';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils/cn';
import DailyPnlTab from './platform/DailyPnlTab';
import DashboardTab from './platform/DashboardTab';
import UserRosterTab from './platform/UserRosterTab';

type TabId = 'dashboard' | 'daily' | 'roster';

interface TabSpec {
  id: TabId;
  label: string;
  description: string;
}

const TABS: TabSpec[] = [
  { id: 'dashboard', label: 'Dashboard', description: 'Platform-wide KPIs + trends' },
  { id: 'daily', label: 'Daily PnL', description: 'Per-user P&L on a chosen date' },
  { id: 'roster', label: 'User Roster', description: 'Every user, lifetime stats, no date filter' },
];

// TEMPORARY (perf): the Daily PnL and User Roster tabs run per-user / per-date
// fan-out queries that are too heavy on the database at current scale. Disabled
// until the query cost is fixed. Re-enable by emptying this array — the tab
// specs, content components, and routing are all left intact. The tab buttons
// are hidden AND deep links (?tab=daily / ?tab=roster) fall back to Dashboard,
// so a disabled tab can never mount and fire its heavy queries.
const DISABLED_TABS: TabId[] = ['daily', 'roster'];

const VISIBLE_TABS: TabSpec[] = TABS.filter((t) => !DISABLED_TABS.includes(t.id));

function readTabFromSearch(params: URLSearchParams): TabId {
  const raw = (params.get('tab') || '').toLowerCase();
  const requested: TabId = raw === 'roster' ? 'roster' : raw === 'daily' ? 'daily' : 'dashboard';
  // A disabled tab (incl. a hand-typed / bookmarked deep link) lands on
  // Dashboard so its heavy content component never mounts.
  return DISABLED_TABS.includes(requested) ? 'dashboard' : requested;
}

const SuperAdminPlatformPnlPage: React.FC = () => {
  const { isSuperAdmin, isLoading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = readTabFromSearch(searchParams);

  // Each tab pushes its action buttons (Refresh / Export CSV) up to the
  // PageHeader via this callback. Using a ref-like setter keeps the tab in
  // charge of when its actions are valid (e.g. Export disabled when 0 rows).
  const [tabActions, setTabActions] = useState<React.ReactNode>(null);
  const registerActions = useCallback((node: React.ReactNode) => {
    setTabActions(node);
  }, []);

  const onTabClick = (id: TabId) => {
    if (id === activeTab) return;
    // Tab switch resets the actions slot — the new tab's effect will
    // re-register on mount. Without this, stale buttons from the previous
    // tab would flash for one frame.
    setTabActions(null);
    const next = new URLSearchParams(searchParams);
    if (id === 'dashboard') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  return (
    <StandardLayout>
      <PageHeader
        title="Platform"
        pageType="management"
        icon={<BarChart3 className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          { label: 'Platform Owner' },
        ]}
        actions={tabActions}
      />

      {/* Tab bar — minimal underline pattern. Hidden when only one tab is
          visible (e.g. while DISABLED_TABS hides the others): a lone
          role="tab" is a degenerate one-item tablist and reads as a broken
          orphaned tab. Restores automatically once >1 tab is enabled. */}
      {VISIBLE_TABS.length > 1 && (
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1" role="tablist" aria-label="Platform sections">
          {VISIBLE_TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabClick(tab.id)}
                className={cn(
                  'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                  active
                    ? 'border-blue-600 text-blue-700 dark:text-blue-300 dark:border-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
                )}
                title={tab.description}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
      )}

      {activeTab === 'dashboard' && <DashboardTab registerActions={registerActions} />}
      {activeTab === 'daily' && <DailyPnlTab registerActions={registerActions} />}
      {activeTab === 'roster' && <UserRosterTab registerActions={registerActions} />}
    </StandardLayout>
  );
};

export default SuperAdminPlatformPnlPage;
